import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { AutoComplete, Input } from "antd";
import { SizeType } from "antd/es/config-provider/SizeContext";
import { LoadingOutlined } from "@ant-design/icons";
import debounce from "lodash.debounce";

type OptionType = {
    value: string;
    label: React.ReactNode; // for dropdown
    display: string; // for input box
    raw?: any;        // original object
};

type Props = {
    value?: string | null;
    onChange?: (value: string | null, option: any | null) => void;
    placeholder?: string;
    style?: React.CSSProperties;
    fetchOptions: (query: string) => Promise<any[]>;
    mapOption: (item: any) => OptionType;
    debounceTime?: number;
    size?: SizeType,
    loading?: boolean
};

const TypeAheadInput: React.FC<Props> = ({
    value,
    onChange,
    placeholder = "Search...",
    style = { width: "100%" },
    fetchOptions,
    mapOption,
    debounceTime = 400,
    size = "middle",
    loading = false
}) => {
    const [optionsData, setOptionsData] = useState<OptionType[]>([]);
    const [inputValue, setInputValue] = useState<string>("");
    const inputRef = useRef<any>(null);

    const debouncedFetch = useMemo(
        () =>
            debounce(async (searchText: string) => {
                if (!searchText) {
                    setOptionsData([]);
                    return;
                }
                try {
                    const results = await fetchOptions(searchText) || [];
                    setOptionsData(results.map(mapOption));
                } catch (err) {
                    console.error("Fetch failed", err);
                }
                setTimeout(() => {
                    inputRef.current?.focus();
                }, 0);
            }, debounceTime),
        [fetchOptions]
    );

    const handleSearch = (searchText: string) => {
        setInputValue(searchText);
        debouncedFetch(searchText);
    };

    const handleSelect = (value: string, option: any) => {
        onChange?.(value, option);
        setInputValue(option.display);
    };

    const options = useMemo(() => optionsData, [optionsData]);

    return (
        <AutoComplete
            style={style}
            options={options}
            value={inputValue}
            placeholder={placeholder}
            onSearch={handleSearch}
            onSelect={handleSelect}
            onChange={(text) => {
                setInputValue(text);
                if (!text) onChange?.(null, null);
            }}
            size={size}
            showAction={["focus"]}
            filterOption={false}
            ref={inputRef}
        >
            <Input size={size} suffix={loading && <LoadingOutlined />} />
        </AutoComplete>
    );
};

export default TypeAheadInput;
